import React, {forwardRef, useState, useEffect} from 'react';
import {ScrollView, View, TouchableOpacity} from 'react-native';
import EStyleSheet from 'react-native-extended-stylesheet';
import DatePicker from 'react-datepicker';
import './automationDatePicker.css';
import TimeZonesService from '../../../../../../services/TimeZonesService';

import Modal from '../../../../../../components/Modal';
import ContentSection from '../../../../../../components/ContentSection';
import AppText from '../../../../../../components/AppText';
import {textStyles} from '../../../../../../styles/text';
import AppTextInput from '../../../../../../components/AppTextInput';
import {basicStyles} from '../../../../../../styles/basic';
import AppButton from '../../../../../../components/AppButton';
import AppPickerInput from '../../../../../../components/AppPickerInput';
import {
    conditionOperatorsFor, defaultTimeValue, defaultDateValue, calendarDateKey, formatConditionDate, readConditionValue, weekdays, timezones,
} from './automationTime';

const DateTrigger = forwardRef(({onClick}, ref) => (
    <button type="button" className="automation-add-date" ref={ref} onClick={onClick}>
        <span>+ Add date</span>
    </button>
));

const pickerAppearance = {
    labelStyle: {fontSize: 12, color: '#9aa4b2', marginBottom: 3},
    inputTextStyle: {fontSize: 15, color: '#1f2937'},
    arrowBordered: false,
    arrowColor: '#6b7280',
};

function ConditionModal({onClose, onSubmit, conditionOperators = [], condition = null, isEdit = false, automationDefs = null}) {
    const definitions = automationDefs || {conditionTypes: [], logicalOperators: []};
    const [availableZones, setAvailableZones] = useState(timezones);
    useEffect(() => {
        let active = true;
        TimeZonesService.getTimeZones().then(zones => {
            if (active) setAvailableZones(zones.map(zone => ({label: zone.time_zone_title, value: zone.time_zone_name})));
        }).catch(() => {});
        return () => {active = false;};
    }, []);
    const [conditionData, setConditionData] = useState(() => isEdit && condition ? {...condition} : {
        message_automation_condition_type_id: definitions.conditionTypes[0]?.message_automation_condition_type_id?.toString() || '',
        message_automation_condition_operator_id: conditionOperators[0]?.message_automation_condition_operator_id?.toString() || '',
        message_automation_condition_operator_slug: conditionOperators[0]?.message_automation_condition_operator_slug || '',
        message_automation_condition_value: '',
        message_automation_condition_logical_operator_id: definitions.logicalOperators?.find(op => op.message_automation_condition_logical_operator_slug === 'and')?.message_automation_condition_logical_operator_id?.toString() || '1',
    });
    const selectedType = definitions.conditionTypes.find(type => String(type.message_automation_condition_type_id) === String(conditionData.message_automation_condition_type_id));
    const slug = selectedType?.message_automation_condition_type_slug;
    const operators = conditionOperatorsFor(slug, conditionOperators);
    const isConversationState = slug === 'conversation-state';
    const time = readConditionValue(conditionData.message_automation_condition_value, defaultTimeValue());
    const date = readConditionValue(conditionData.message_automation_condition_value, defaultDateValue());
    const zoneOptions = availableZones.some(zone => zone.value === time.tz) ? availableZones : [...availableZones, {label: time.tz, value: time.tz}];
    const canSave = slug !== 'date' || (Array.isArray(date.dates) && date.dates.length > 0 && operators.some(item => String(item.message_automation_condition_operator_id) === String(conditionData.message_automation_condition_operator_id)));

    const updateValue = value => setConditionData(current => ({...current, message_automation_condition_value: value}));
    const changeField = value => {
        const type = definitions.conditionTypes.find(item => String(item.message_automation_condition_type_id) === String(value));
        const nextSlug = type?.message_automation_condition_type_slug;
        const nextOperators = conditionOperatorsFor(nextSlug, conditionOperators);
        const operator = nextSlug === 'conversation-state'
            ? nextOperators.find(item => item.message_automation_condition_operator_slug === '=')
            : nextSlug === 'date' ? nextOperators.find(item => item.message_automation_condition_operator_slug === 'on')
                : nextOperators[0];
        setConditionData(current => ({
            ...current,
            message_automation_condition_type_id: value,
            message_automation_condition_operator_id: operator?.message_automation_condition_operator_id?.toString() || '',
            message_automation_condition_operator_slug: operator?.message_automation_condition_operator_slug || '',
            message_automation_condition_value: nextSlug === 'time' ? JSON.stringify(defaultTimeValue())
                : nextSlug === 'date' ? JSON.stringify(defaultDateValue())
                : nextSlug === 'conversation-state' ? 'new' : '',
        }));
    };
    const changeOperator = value => {
        const operator = operators.find(item => String(item.message_automation_condition_operator_id) === String(value));
        setConditionData(current => ({
            ...current,
            message_automation_condition_operator_id: value,
            message_automation_condition_operator_slug: operator?.message_automation_condition_operator_slug || '',
        }));
    };
    const updateTime = (key, value) => updateValue(JSON.stringify({...time, [key]: value}));
    const updateDate = changes => updateValue(JSON.stringify({...date, ...changes}));
    const renderTime = (key, label) => {
        const [hour, minute, amPm] = time[key].split(/[: ]/);
        const fields = [
            ['Hour', hour, Array.from({length: 12}, (_, index) => String(index + 1))],
            ['Min', minute, Array.from({length: 60}, (_, index) => String(index).padStart(2, '0'))],
            ['AM/PM', amPm, ['AM', 'PM']],
        ];
        return (
            <View style={styles.timeSection}>
                <AppText style={styles.subheading}>{label}</AppText>
                <View style={basicStyles.flexRow}>
                    {fields.map(([field, selected, values], index) => (
                        <AppPickerInput {...pickerAppearance} key={field} label={field} selectedValue={selected}
                            items={values.map(value => ({label: value, value}))}
                            onValueChange={value => {
                                const parts = [hour, minute, amPm];
                                parts[index] = value;
                                updateTime(key, `${parts[0]}:${parts[1]} ${parts[2]}`);
                            }}
                            wrapperStyle={[styles.timeInput, index < 2 && {marginRight: 10}]}
                        />
                    ))}
                </View>
            </View>
        );
    };

    return (
        <Modal active={true} onClose={onClose} style={styles.overlay} innerStyle={{width: '100%', alignItems: 'center'}}>
            <ContentSection style={styles.contentWrapper} onClick={event => event.stopPropagation()}>
                <ScrollView style={styles.scrollArea}>
                    <AppText style={[textStyles.pageTitle, {fontSize: 22, fontWeight: 'bold', marginBottom: 20}]}>
                        {isEdit ? 'Edit' : 'Add New'} Condition
                    </AppText>
                    <AppPickerInput {...pickerAppearance} label="Field" selectedValue={String(conditionData.message_automation_condition_type_id)}
                        onValueChange={changeField}
                        items={definitions.conditionTypes.map(type => ({label: type.message_automation_condition_type_title, value: String(type.message_automation_condition_type_id)}))}
                        wrapperStyle={styles.inputWrapper}
                    />
                    {slug === 'date' ? (
                        <AppPickerInput {...pickerAppearance} label="Condition" selectedValue={date.recurring ? 'recurring' : 'fixed'}
                            onValueChange={mode => updateDate({recurring: mode === 'recurring', dates: []})}
                            items={[{label: 'Fixed', value: 'fixed'}, {label: 'Recurring', value: 'recurring'}]}
                            wrapperStyle={styles.inputWrapper}
                        />
                    ) : !isConversationState && (
                        <AppPickerInput {...pickerAppearance} label="Condition" selectedValue={String(conditionData.message_automation_condition_operator_id)}
                            onValueChange={changeOperator} items={operators}
                            labelExtractor="message_automation_condition_operator_title"
                            valueExtractor={item => String(item.message_automation_condition_operator_id)}
                            wrapperStyle={styles.inputWrapper}
                        />
                    )}
                    {slug === 'date' ? (
                        <View>
                            <View style={styles.calendar}>
                                <DatePicker key={date.recurring ? 'annual' : 'fixed'} selected={null} disabledKeyboardNavigation
                                    openToDate={new Date()} shouldCloseOnSelect={false}
                                    customInput={<DateTrigger />} showPopperArrow={false}
                                    portalId="automation-date-calendar-portal" popperPlacement="bottom-start"
                                    popperClassName="automation-date-popper" calendarClassName="automation-date-calendar"
                                    showMonthDropdown showYearDropdown={!date.recurring} dropdownMode="select"
                                    dayClassName={day => (date.dates || []).includes(calendarDateKey(day, date.recurring)) ? 'react-datepicker__day--selected' : undefined}
                                    onChange={day => {
                                        if (day) updateDate({dates: [...new Set([...(date.dates || []), calendarDateKey(day, date.recurring)])].sort()});
                                    }}
                                />
                            </View>
                            <View style={styles.selectedDates}>
                                {(date.dates || []).map(value => (
                                    <View key={value} style={styles.selectedDate}>
                                        <AppText style={styles.selectedDateText}>{formatConditionDate(value, date.recurring)}</AppText>
                                        <TouchableOpacity accessibilityRole="button" accessibilityLabel={`Remove ${value}`}
                                            onPress={() => updateDate({dates: date.dates.filter(item => item !== value)})}>
                                            <AppText style={styles.removeDate}>×</AppText>
                                        </TouchableOpacity>
                                    </View>
                                ))}
                            </View>
                        </View>
                    ) : slug === 'time' ? (
                        <View>
                            {renderTime('start', 'Start time')}
                            {renderTime('end', 'End time')}
                            <AppPickerInput {...pickerAppearance} label="Timezone" selectedValue={time.tz}
                                onValueChange={value => updateTime('tz', value)} items={zoneOptions}
                                wrapperStyle={styles.inputWrapper}
                            />
                            <AppText style={styles.hint}>Active on days</AppText>
                            <View style={[basicStyles.flexRow, {marginBottom: 8}]}>
                                {weekdays.map((day, index) => (
                                    <TouchableOpacity key={day} accessibilityRole="button" accessibilityLabel={day}
                                        accessibilityState={{selected: time.days.includes(index)}}
                                        style={[styles.day, time.days.includes(index) && styles.selectedDay, index < 6 && {marginRight: 6}]}
                                        onPress={() => updateTime('days', time.days.includes(index)
                                            ? time.days.filter(value => value !== index) : [...time.days, index].sort())}>
                                        <AppText style={[styles.dayText, time.days.includes(index) && styles.selectedText]}>{day}</AppText>
                                    </TouchableOpacity>
                                ))}
                            </View>
                        </View>
                    ) : isConversationState ? (
                        <AppPickerInput {...pickerAppearance} label="Value" selectedValue={conditionData.message_automation_condition_value}
                            onValueChange={updateValue}
                            items={[{label: 'New', value: 'new'}, {label: 'Open', value: 'open'}, {label: 'Closed', value: 'closed'}]}
                            wrapperStyle={styles.inputWrapper}
                        />
                    ) : (
                        <AppTextInput label="Value" labelStyle={pickerAppearance.labelStyle} inputStyle={pickerAppearance.inputTextStyle}
                            value={conditionData.message_automation_condition_value}
                            onChangeText={updateValue} wrapperStyle={styles.inputWrapper} placeholder="Enter the value to match"
                        />
                    )}
                    <View style={[basicStyles.flexRow, {marginTop: 22}]}>
                        <AppButton label="Cancel" action={onClose} style={styles.cancelButton} textStyle={styles.cancelText} theme="transBlue" animated={false} />
                        <AppButton label="Save Condition" style={styles.saveButton} textStyle={styles.buttonText} animated={false} disabled={!canSave} action={() => {if (canSave) {onSubmit(conditionData); onClose();}}} />
                    </View>
                </ScrollView>
            </ContentSection>
        </Modal>
    );
}

export default ConditionModal;

const styles = EStyleSheet.create({
    overlay: {justifyContent: 'flex-start', paddingTop: 40, paddingHorizontal: 16, backgroundColor: 'rgba(60,70,100,.45)'},
    contentWrapper: {paddingHorizontal: 30, paddingVertical: 28, width: 460, maxWidth: '100%', maxHeight: '90vh', borderRadius: 14, overflow: 'hidden'},
    scrollArea: {minHeight: 0, flexShrink: 1},
    calendar: {alignItems: 'flex-start'},
    selectedDates: {flexDirection: 'row', flexWrap: 'wrap', marginTop: 12},
    selectedDate: {flexDirection: 'row', alignItems: 'center', paddingVertical: 6, paddingHorizontal: 12, marginRight: 8, marginBottom: 8, borderWidth: 1, borderColor: '#bfdbfe', backgroundColor: '#eff6ff', borderRadius: 20},
    selectedDateText: {fontSize: 13, fontWeight: '500', color: '#1e3a8a', marginRight: 8},
    removeDate: {fontSize: 15, color: '#1e3a8a'},
    cancelButton: {height: 42, borderRadius: 8, borderColor: '#e2e6ee', paddingHorizontal: 22, backgroundColor: '#fff', marginRight: 12},
    saveButton: {height: 42, borderRadius: 8, borderColor: '#2563eb', backgroundColor: '#2563eb', paddingHorizontal: 26},
    cancelText: {fontSize: 14, fontWeight: '600', color: '#374151'},
    buttonText: {fontSize: 14, fontWeight: '600'},
    inputWrapper: {marginBottom: 14, borderRadius: 10, borderColor: '#e2e6ee', height: 62, paddingVertical: 10},
    timeSection: {marginBottom: 14},
    subheading: {fontSize: 13, fontWeight: 'bold', marginBottom: 8},
    timeInput: {flex: 1, minWidth: 0, borderRadius: 10, height: 62, paddingVertical: 10},
    hint: {fontSize: 12, color: '#9aa4b2', marginBottom: 10},
    day: {flex: 1, paddingVertical: 8, borderWidth: 1, borderColor: '#e2e6ee', borderRadius: 8, alignItems: 'center'},
    dayText: {fontSize: 12, color: '#6b7280'},
    selectedDay: {borderColor: '#2563eb', backgroundColor: '#eff6ff'},
    selectedText: {color: '#1d4ed8'},
});
